import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { InfoIcon, WarningIcon } from '@udacity/chakra-uds-icons';

import { useNewsletterSignUpHandler } from '~/hooks/use-newsletter-signup';

const newsletterFormSchema = z.object({
  email: z.string().trim().min(1, 'Email is required').email('Please enter a valid email address'),
});

type NewsletterFormData = z.infer<typeof newsletterFormSchema>;

export function NewsletterSignUp() {
  const { t } = useTranslation();
  const handleNewsletterSubmit = useNewsletterSignUpHandler();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<NewsletterFormData>({
    resolver: zodResolver(newsletterFormSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = handleSubmit(async (data) => {
    await handleNewsletterSubmit(data);
    reset();
  });

  const submitButtonLabel = t('footer.newsletter.subscribeToNewsletter');

  return (
    <Box as='form' maxWidth={{ base: '100%', md: '470px' }} width='100%' onSubmit={onSubmit}>
      <FormControl isInvalid={Boolean(errors.email)}>
        <Flex flexDirection='column' gap='16px'>
          <Flex flex={1} flexDirection='column' gap='8px'>
            <Flex justifyContent='space-between'>
              <FormLabel
                alignItems='center'
                color='white'
                display='flex'
                fontSize={{ base: '14px', md: '13px' }}
                fontWeight='400'
                mb='0'
              >
                {t('footer.newsletter.submitYourEmail')}
              </FormLabel>
              <Popover placement='top' strategy='fixed'>
                <PopoverTrigger>
                  <IconButton
                    aria-label='Sign up Terms and Conditions.'
                    color='gray.300'
                    icon={<InfoIcon height='16px' width='16px' />}
                    paddingInline='0px'
                    size='xs'
                    variant='ghost'
                  />
                </PopoverTrigger>
                <PopoverContent bg='white'>
                  <PopoverArrow />
                  <PopoverCloseButton />
                  <PopoverBody color='gray.900' fontSize='sm' fontWeight='500' mt='16px'>
                    <Trans
                      i18nKey='footer.newsletter.terms'
                      values={{ submitButtonLabel }}
                      components={{
                        termsLink: <Box as={Link} href='/en-US/legal/terms-of-use' textDecoration='underline' />,
                        privacyLink: <Box as={Link} href='/legal/privacy' textDecoration='underline' />,
                      }}
                    />
                  </PopoverBody>
                </PopoverContent>
              </Popover>
            </Flex>

            <Flex flexDirection='column'>
              <Flex
                flexDirection={{ base: 'column', md: 'row' }}
                gap={{ base: '12px', md: '8px' }}
                wrap={{ base: 'nowrap', md: 'wrap' }}
              >
                <Input
                  _placeholder={{ color: 'gray.300' }}
                  autoComplete='email'
                  bgColor='blackAlpha.300'
                  borderColor='whiteAlpha.400'
                  color='white'
                  flex={{ base: '0 0 auto', md: '1 1 269px' }}
                  focusBorderColor='blue.200'
                  height='38px'
                  maxW='100%'
                  placeholder={t('footer.newsletter.emailPlaceholder') ?? undefined}
                  type='email'
                  width='100%'
                  {...register('email')}
                />
                <Button
                  _hover={{ bgColor: 'blue.600' }}
                  bgColor='blue.500'
                  color='white'
                  flex={{ base: '0 0 auto', md: '1 0 auto' }}
                  flexShrink={0}
                  fontSize='12px'
                  height='38px'
                  type='submit'
                >
                  {submitButtonLabel}
                </Button>
              </Flex>
              {errors.email && (
                <FormErrorMessage fontSize='sm' mt='8px'>
                  <>
                    <WarningIcon h='26px' w='26px' />
                    {errors.email.message}
                  </>
                </FormErrorMessage>
              )}
            </Flex>
          </Flex>
        </Flex>
      </FormControl>
    </Box>
  );
}
